import React, { useMemo } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useAuthUser } from 'react-auth-kit';
import { useToolCallsQuery, useToolCallSummaryQuery } from 'src/api/queries/agentActivityQueries';
import { useUserManagementQuery } from 'src/api/queries/userManagementQueries';
import { getToolLabel } from 'src/chat/actionLabels';
import Button from 'src/components/Button';
import PageTitle from 'src/components/PageTitle';
import Select from 'src/components/Select';
import { ConnectedAppsModal } from 'src/components/ConnectedApps';
import useModal from 'src/hooks/useModal';
import useRoleManager from 'src/hooks/useRoleManager';
import type {
  AgentKind,
  AgentSummary,
  ToolCall,
  ToolCallAgent,
  ToolCallFilters,
  ToolCallStatus,
} from 'src/types/AgentActivity';
import messages from './messages';
import styles from './styles.module.scss';

const ANY = '__any__';
const PERIODS: Record<string, number> = { '24h': 1, '7d': 7, '30d': 30, '90d': 90 };
const DEFAULT_PERIOD = '7d';
const MAX_TARGETS_SHOWN = 3;

const TARGET_LINKS: Record<string, (id: string) => string> = {
  asset: (id) => `/asset/${id}`,
  collection: (id) => `/collection/${id}`,
  share: (id) => `/shares/${id}`,
  schedule: (id) => `/ai/schedules/${id}`,
};

/** The URL is the state, so a link from Connected apps (or a shared URL) opens
 *  the page already filtered. */
function useParamsState() {
  const location = useLocation();
  const history = useHistory();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const set = (changes: Record<string, string | undefined>) => {
    const next = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === undefined || value === '' || value === ANY) next.delete(key);
      else next.set(key, value);
    });
    history.replace({ pathname: location.pathname, search: next.toString() });
  };
  return { params, set };
}

/** Filters that pick out one agent, from a summary row or a call's agent. */
function agentFilters(agent: Pick<ToolCallAgent, 'kind' | 'oauth_session_id' | 'schedule_id'>) {
  return {
    agent_kind: agent.kind,
    oauth_session_id: agent.oauth_session_id ? String(agent.oauth_session_id) : undefined,
    schedule_id: agent.schedule_id ?? undefined,
  };
}

function sameAgent(a: AgentSummary['agent'], params: URLSearchParams) {
  return (
    a.kind === params.get('agent_kind') &&
    String(a.oauth_session_id ?? '') === (params.get('oauth_session_id') ?? '') &&
    (a.schedule_id ?? '') === (params.get('schedule_id') ?? '')
  );
}

function csvCell(value: unknown) {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export const AgentActivity = () => {
  const { formatMessage } = useIntl();
  const { isAdmin } = useRoleManager();
  const authUser = useAuthUser()();
  const myId = authUser?.userId;
  const { showModal } = useModal();
  const { params, set } = useParamsState();

  const period = PERIODS[params.get('period') ?? ''] ? (params.get('period') as string) : DEFAULT_PERIOD;
  // Fixed per period choice (not per render), so the query key is stable.
  const since = useMemo(() => moment.utc().subtract(PERIODS[period], 'days').startOf('minute').toISOString(), [period]);

  const userIdParam = params.get('user_id') ? Number(params.get('user_id')) : undefined;
  const otherUser = userIdParam !== undefined && userIdParam !== myId ? userIdParam : undefined;
  const isAccount = isAdmin && params.get('account') === 'true' && otherUser === undefined;

  const scope: ToolCallFilters = {
    ...(isAccount ? { account: true } : {}),
    ...(otherUser !== undefined ? { user_id: otherUser } : {}),
    since,
  };
  const filters: ToolCallFilters = {
    ...scope,
    agent_kind: (params.get('agent_kind') as AgentKind) || undefined,
    oauth_session_id: params.get('oauth_session_id') ? Number(params.get('oauth_session_id')) : undefined,
    schedule_id: params.get('schedule_id') || undefined,
    status: (params.get('status') as ToolCallStatus) || undefined,
    tool: params.get('tool') || undefined,
  };

  const summary = useToolCallSummaryQuery(scope);
  const calls = useToolCallsQuery(filters);
  const users = useUserManagementQuery(
    { page: 1, limit_per_page: 30, email: '', first_name: '', last_name: '', enabled: false, include_removed: true },
    undefined,
    { enabled: isAdmin && isAccount },
  );

  const agents = summary.data?.data ?? [];
  const items: ToolCall[] = useMemo(() => (calls.data?.pages ?? []).flatMap((page) => page.data.items), [calls.data]);
  const selectedAgent = agents.find((a) => sameAgent(a.agent, params));
  const agentSelected = !!(params.get('oauth_session_id') || params.get('schedule_id'));

  const totals = agents.reduce((acc, a) => ({ calls: acc.calls + a.calls, errors: acc.errors + a.errors }), {
    calls: 0,
    errors: 0,
  });
  const rate = (calls: number, errors: number) => (calls ? `${Math.round((errors / calls) * 100)}%` : '–');

  const kindLabel = (kind: AgentKind) =>
    formatMessage(
      {
        dashboard: messages.kindDashboard,
        scheduled: messages.kindScheduled,
        external: messages.kindExternal,
        api_key: messages.kindApiKey,
      }[kind],
    );
  const statusLabel = (status: ToolCallStatus) =>
    formatMessage(
      { success: messages.statusSuccess, error: messages.statusError, rejected: messages.statusRejected }[status],
    );
  const statusClass = (status: ToolCallStatus) =>
    ({ success: styles.statusSuccess, error: styles.statusError, rejected: styles.statusRejected }[status]);

  const toolOptions = useMemo(() => {
    const tools = new Set(items.map((i) => i.tool));
    if (filters.tool) tools.add(filters.tool);
    return [
      { label: formatMessage(messages.any), value: ANY },
      ...Array.from(tools)
        .sort()
        .map((tool) => ({ label: getToolLabel(tool), value: tool })),
    ];
  }, [items, filters.tool, formatMessage]);

  const userOptions = useMemo(() => {
    const seen = new Map<number, string>();
    ((users.data?.users ?? []) as Array<{ id: number; email: string }>).forEach((u) => seen.set(u.id, u.email));
    items.forEach((i) => i.user_email && seen.set(i.user_id, i.user_email));
    return [
      { label: formatMessage(messages.allUsers), value: ANY },
      ...Array.from(seen, ([id, email]) => ({ label: email, value: String(id) })).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    ];
  }, [users.data, items, formatMessage]);

  const signOutAction = (agent: AgentSummary['agent']) => {
    if (agent.kind !== 'external' || !agent.oauth_session_id) return null;
    // Your own connections live on your profile; a member's open in the same
    // modal the Users page uses.
    if (otherUser === undefined && !isAccount) {
      return (
        <Link className={styles.linkButton} to="/me">
          {formatMessage(messages.signOutApp)}
        </Link>
      );
    }
    const userId = agent.user_id ?? otherUser;
    if (userId === undefined || userId === null) return null;
    const email =
      items.find((i) => i.user_id === userId)?.user_email ??
      userOptions.find((o) => o.value === String(userId))?.label ??
      '';
    return (
      <button
        type="button"
        className={styles.linkButton}
        onClick={() => showModal(<ConnectedAppsModal user={{ id: userId, email }} />)}
      >
        {formatMessage(messages.signOutApp)}
      </button>
    );
  };

  const exportCsv = () => {
    const header = ['time', 'user', 'agent', 'agent_type', 'action', 'tool', 'result', 'error', 'took_ms', 'targets'];
    const rows = items.map((i) => [
      i.ts,
      i.user_email,
      i.agent.name,
      i.agent.kind,
      getToolLabel(i.tool),
      i.tool,
      i.status,
      i.error_message || i.error_code,
      i.latency_ms,
      Object.entries(i.target_ids)
        .map(([kind, ids]) => `${kind}:${(ids ?? []).join(' ')}`)
        .join('; '),
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-activity-${moment().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const errorText = (error: any) => formatMessage(error?.status === 403 ? messages.forbidden : messages.loadError);

  return (
    <div className={styles.pageWrapper} data-test="agent-activity">
      <PageTitle title={formatMessage(messages.title)} />
      <div className={styles.content}>
        <p className={styles.intro}>{formatMessage(messages.intro)}</p>

        <div className={styles.toolbar}>
          {isAdmin && (
            <div className={styles.scope}>
              <Button
                size="small"
                mode={isAccount || otherUser !== undefined ? 'tertiary' : 'primary'}
                onClick={() => set({ account: undefined, user_id: undefined })}
              >
                {formatMessage(messages.scopeMine)}
              </Button>
              <Button
                size="small"
                mode={isAccount ? 'primary' : 'tertiary'}
                onClick={() => set({ account: 'true', user_id: undefined })}
              >
                {formatMessage(messages.scopeAccount)}
              </Button>
            </div>
          )}
          {otherUser !== undefined && (
            <span className={styles.chip}>
              {formatMessage(messages.showingUser, {
                email: items[0]?.user_email ?? `#${otherUser}`,
              })}
              <button type="button" className={styles.chipClear} onClick={() => set({ user_id: undefined })}>
                {formatMessage(messages.clear)}
              </button>
            </span>
          )}
          <Select
            label={formatMessage(messages.period)}
            value={period}
            onChange={(value) => set({ period: value === DEFAULT_PERIOD ? undefined : value })}
            options={[
              { label: formatMessage(messages.last24h), value: '24h' },
              { label: formatMessage(messages.last7d), value: '7d' },
              { label: formatMessage(messages.last30d), value: '30d' },
              { label: formatMessage(messages.last90d), value: '90d' },
            ]}
          />
        </div>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>{formatMessage(messages.agentsTitle)}</h3>
            {agents.length > 0 && (
              <span className={styles.meta}>
                {formatMessage(messages.totals, {
                  calls: totals.calls,
                  agents: agents.length,
                  rate: rate(totals.calls, totals.errors),
                })}
              </span>
            )}
          </div>
          {summary.isLoading ? (
            <p className={styles.empty}>{formatMessage(messages.loading)}</p>
          ) : summary.error ? (
            <p className={styles.empty} role="alert">
              {errorText(summary.error)}
            </p>
          ) : agents.length === 0 ? (
            <p className={styles.empty}>{formatMessage(messages.noAgents)}</p>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table} data-test="agent-summary">
                <thead>
                  <tr>
                    <th>{formatMessage(messages.agent)}</th>
                    <th className={styles.numeric}>{formatMessage(messages.calls)}</th>
                    <th className={styles.numeric}>{formatMessage(messages.errors)}</th>
                    <th className={styles.numeric}>{formatMessage(messages.rejected)}</th>
                    <th className={styles.numeric}>{formatMessage(messages.errorRate)}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a) => (
                    <tr
                      key={`${a.agent.kind}-${a.agent.oauth_session_id}-${a.agent.schedule_id}`}
                      className={sameAgent(a.agent, params) ? styles.selectedRow : undefined}
                    >
                      <td>
                        <div className={styles.agentCell}>
                          <span className={styles.agentName}>{a.agent.name}</span>
                          <span className={styles.badge}>{kindLabel(a.agent.kind)}</span>
                        </div>
                      </td>
                      <td className={styles.numeric}>{a.calls}</td>
                      <td className={styles.numeric}>{a.errors}</td>
                      <td className={styles.numeric}>{a.rejected}</td>
                      <td className={styles.numeric}>{rate(a.calls, a.errors)}</td>
                      <td>
                        <div className={styles.rowActions}>
                          <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => set({ ...agentFilters(a.agent), tool: undefined })}
                          >
                            {formatMessage(messages.viewActions)}
                          </button>
                          {a.agent.kind === 'scheduled' && a.agent.schedule_id && (
                            <Link className={styles.linkButton} to={`/ai/schedules/${a.agent.schedule_id}`}>
                              {formatMessage(messages.openSchedule)}
                            </Link>
                          )}
                          {signOutAction(a.agent)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <span className={styles.meta}>{formatMessage(messages.agentsHint)}</span>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3>{formatMessage(messages.actionsTitle)}</h3>
            {isAdmin && items.length > 0 && (
              <Button size="small" mode="tertiary" onClick={exportCsv} tooltip={formatMessage(messages.exportHint)}>
                {formatMessage(messages.exportCsv)}
              </Button>
            )}
          </div>

          <div className={styles.toolbar}>
            {agentSelected && (
              <span className={styles.chip} data-test="agent-filter">
                {formatMessage(messages.agentFilter, {
                  name: selectedAgent?.agent.name ?? items[0]?.agent.name ?? '…',
                })}
                <button
                  type="button"
                  className={styles.chipClear}
                  onClick={() => set({ agent_kind: undefined, oauth_session_id: undefined, schedule_id: undefined })}
                >
                  {formatMessage(messages.clear)}
                </button>
              </span>
            )}
            {!agentSelected && (
              <Select
                label={formatMessage(messages.filterKind)}
                value={params.get('agent_kind') ?? ANY}
                onChange={(value) => set({ agent_kind: value })}
                options={[
                  { label: formatMessage(messages.any), value: ANY },
                  { label: kindLabel('dashboard'), value: 'dashboard' },
                  { label: kindLabel('scheduled'), value: 'scheduled' },
                  { label: kindLabel('external'), value: 'external' },
                ]}
              />
            )}
            <Select
              label={formatMessage(messages.filterStatus)}
              value={params.get('status') ?? ANY}
              onChange={(value) => set({ status: value })}
              options={[
                { label: formatMessage(messages.any), value: ANY },
                { label: statusLabel('success'), value: 'success' },
                { label: statusLabel('error'), value: 'error' },
                { label: statusLabel('rejected'), value: 'rejected' },
              ]}
            />
            <Select
              label={formatMessage(messages.filterTool)}
              value={params.get('tool') ?? ANY}
              onChange={(value) => set({ tool: value })}
              options={toolOptions}
            />
            {isAccount && (
              <Select
                label={formatMessage(messages.filterUser)}
                value={ANY}
                onChange={(value) => value !== ANY && set({ user_id: value, account: undefined })}
                options={userOptions}
              />
            )}
          </div>

          {calls.isLoading ? (
            <p className={styles.empty}>{formatMessage(messages.loading)}</p>
          ) : calls.error ? (
            <p className={styles.empty} role="alert">
              {errorText(calls.error)}
            </p>
          ) : items.length === 0 ? (
            <p className={styles.empty}>{formatMessage(messages.noActions)}</p>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table} data-test="agent-actions">
                <thead>
                  <tr>
                    <th>{formatMessage(messages.colTime)}</th>
                    <th>{formatMessage(messages.agent)}</th>
                    {(isAccount || otherUser !== undefined) && <th>{formatMessage(messages.colUser)}</th>}
                    <th>{formatMessage(messages.colAction)}</th>
                    <th>{formatMessage(messages.colTargets)}</th>
                    <th>{formatMessage(messages.colResult)}</th>
                    <th className={styles.numeric}>{formatMessage(messages.colTook)}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((call) => (
                    <tr key={call.id} data-test="agent-action-row">
                      <td title={moment(call.ts).format('D MMM YYYY, HH:mm:ss')} style={{ whiteSpace: 'nowrap' }}>
                        {moment(call.ts).fromNow()}
                      </td>
                      <td>
                        <div className={styles.agentCell}>
                          <span className={styles.agentName}>{call.agent.name}</span>
                          <span className={styles.badge}>{kindLabel(call.agent.kind)}</span>
                        </div>
                      </td>
                      {(isAccount || otherUser !== undefined) && <td>{call.user_email}</td>}
                      <td>
                        {getToolLabel(call.tool)}
                        <span className={styles.toolName}>{call.tool}</span>
                      </td>
                      <td>
                        <Targets call={call} />
                      </td>
                      <td>
                        <span className={statusClass(call.status)}>{statusLabel(call.status)}</span>
                        {(call.error_message || (call.status !== 'success' && call.error_code)) && (
                          <span className={styles.errorText}>{call.error_message || call.error_code}</span>
                        )}
                      </td>
                      <td className={styles.numeric}>
                        {call.latency_ms !== null
                          ? `${(call.latency_ms / 1000).toFixed(call.latency_ms < 10000 ? 1 : 0)}s`
                          : '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {calls.hasNextPage && (
            <div className={styles.footer}>
              <Button
                size="small"
                mode="tertiary"
                loading={calls.isFetchingNextPage}
                onClick={() => calls.fetchNextPage()}
              >
                {formatMessage(messages.loadMore)}
              </Button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const Targets = ({ call }: { call: ToolCall }) => {
  const { formatMessage } = useIntl();
  const labels: Record<string, string> = {
    asset: formatMessage(messages.targetAsset),
    collection: formatMessage(messages.targetCollection),
    share: formatMessage(messages.targetShare),
    schedule: formatMessage(messages.targetSchedule),
    job: formatMessage(messages.targetJob),
  };
  const all = Object.entries(call.target_ids).flatMap(([kind, ids]) => (ids ?? []).map((id) => ({ kind, id })));
  if (all.length === 0) return <span>–</span>;
  const shown = all.slice(0, MAX_TARGETS_SHOWN);
  return (
    <div className={styles.targets}>
      {shown.map(({ kind, id }) => {
        const text = `${labels[kind] ?? kind} ${id.length > 10 ? `${id.slice(0, 8)}…` : id}`;
        const to = TARGET_LINKS[kind]?.(id);
        return to ? (
          <Link key={`${kind}-${id}`} className={styles.targetLink} to={to} title={id}>
            {text}
          </Link>
        ) : (
          <span key={`${kind}-${id}`} title={id}>
            {text}
          </span>
        );
      })}
      {all.length > shown.length && (
        <span className={styles.toolName}>{formatMessage(messages.more, { count: all.length - shown.length })}</span>
      )}
    </div>
  );
};

export default AgentActivity;
